import { useIsMobile } from '@/hooks/useIsMobile';
import { scrollToTop } from '@/utils/scrollToTop';
import cn from 'classnames';
import { useRouter } from 'next/router';
import ReactPaginate from 'react-paginate';
import style from './Pagination.module.scss';

export const Pagination = ({
  total,
  pageSize,
  current,
  foundText,
  hrefBuilder,
  onChange: onChangeProp,
  className,
  onPageChangeScrollTo = 'top',
}: {
  /** Total number of items. */
  total: number;

  /** Number of items per page. */
  pageSize: number;

  /** Page number (starts with 1). */
  current: number;

  foundText?: string;
  hrefBuilder?(pageNumber: number): string;

  /**
   * @param pageNumber Page number (starts with 1).
   */
  onChange?(pageNumber: number): void;
  className?: string;
  onPageChangeScrollTo?: 'top' | React.RefObject<HTMLElement>;
}) => {
  const isMobile = useIsMobile();
  const router = useRouter();
  const onChange =
    onChangeProp ??
    (hrefBuilder ? (pageNumber: number) => router.push(hrefBuilder(pageNumber)) : undefined);

  const handleScroll = () => {
    if (onPageChangeScrollTo === 'top') {
      scrollToTop();
      return;
    }

    onPageChangeScrollTo.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={cn(style.pagination, 'flex items-center space-between flex-col md:flex-row')}>
      {foundText && <div className="font-bold text-base text-gray-01 md:mr-5">{foundText}</div>}
      {total > pageSize && (
        <ReactPaginate
          pageCount={Math.ceil(total / pageSize)}
          forcePage={current - 1}
          hrefBuilder={hrefBuilder}
          onPageChange={({ selected: pageIndex }) => {
            const pageNumber = pageIndex + 1;
            handleScroll();
            onChange?.(pageNumber);
          }}
          pageClassName="ant-pagination-item"
          className={cn('ant-pagination', className)}
          activeClassName="ant-pagination-item-active"
          disabledClassName="ant-pagination-disabled"
          previousClassName="ant-pagination-prev"
          previousLabel={
            <button className="ant-pagination-item-link" type="button">
              <span role="img" aria-label="left" className="anticon anticon-left">
                <svg
                  viewBox="64 64 896 896"
                  focusable="false"
                  data-icon="left"
                  width="1em"
                  height="1em"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8a31.86 31.86 0 000 50.3l450.8 352.1c5.3 4.1 12.9.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z"></path>
                </svg>
              </span>
            </button>
          }
          nextClassName="ant-pagination-next"
          nextLabel={
            <button className="ant-pagination-item-link" type="button">
              <span role="img" aria-label="right" className="anticon anticon-right">
                <svg
                  viewBox="64 64 896 896"
                  focusable="false"
                  data-icon="right"
                  width="1em"
                  height="1em"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M765.7 486.8L314.9 134.7A7.97 7.97 0 00302 141v77.3c0 4.9 2.3 9.6 6.1 12.6l360 281.1-360 281.1c-3.9 3-6.1 7.7-6.1 12.6V883c0 6.7 7.7 10.4 12.9 6.3l450.8-352.1a31.96 31.96 0 000-50.4z"></path>
                </svg>
              </span>
            </button>
          }
          breakClassName="ant-pagination-jump-next ant-pagination-jump-next-custom-icon"
          breakLabel={
            <div className="ant-pagination-item-container">
              <span className="ant-pagination-item-ellipsis" style={{ opacity: 1 }}>
                •••
              </span>
            </div>
          }
          marginPagesDisplayed={isMobile ? 1 : undefined}
        />
      )}
    </div>
  );
};
